import * as Yup from 'yup';
import { App } from '../../../../App';
import { Button, LinkActor, Spacer, Title } from 'internalLibrary';
import { ChangeEvent, createRef, useCallback, useEffect, useRef } from 'react';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info.svg';
import { TwoFAFormValues } from '../../../../types/ui';
import { TYPE_ERROR_CODE_EMAIL_MESSAGE } from '../../../components/AuthProvider';
import { useFormik } from 'formik';
import classNames from 'classnames';
import styles from './TwoFA.module.scss';
import useHandleExistingLoginNavigation from '../../../hooks/useHandleExistingLogin';

const initialValues: TwoFAFormValues = {
  code: ['', '', '', ''],
};

interface TwoFAFormProps {
  isLoggedIn: boolean;
  onSubmit: (values: TwoFAFormValues) => void;
  resendEMAIL: () => void;
  secondsLeftToResendSMS: number;
  error2faCode: TYPE_ERROR_CODE_EMAIL_MESSAGE | undefined;
  status2faCode: string | undefined;
  error2faEmail: TYPE_ERROR_CODE_EMAIL_MESSAGE | undefined;
}

const digitRegex = RegExp(/^[0-9]$/);

export const TwoFAForm = (props: TwoFAFormProps) => {
  const {
    onSubmit,
    resendEMAIL,
    secondsLeftToResendSMS,
    isLoggedIn,
    error2faCode,
    status2faCode,
    error2faEmail,
  } = props;
  const { handleLoginNavigation } = useHandleExistingLoginNavigation();
  const t = App.translateContent('twoFA');

  const availableEmail = secondsLeftToResendSMS === 0;

  useEffect(() => {
    if (!isLoggedIn) return;

    handleLoginNavigation();
  }, [isLoggedIn]);

  const inputRefs = useRef([
    createRef<HTMLInputElement>(),
    createRef<HTMLInputElement>(),
    createRef<HTMLInputElement>(),
    createRef<HTMLInputElement>(),
  ]);

  const { handleSubmit, values, setFieldValue } = useFormik({
    initialValues,
    onSubmit,
  });

  const handleChangeInput = useCallback(
    (value: string, index: number) => {
      if (value === '') {
        const newCode = [...values.code];
        newCode[index] = '';
        setFieldValue('code', newCode);
        if (index > 0) {
          inputRefs.current[index - 1].current?.focus();
        }
        return;
      }

      const prevValue = values.code[index];
      const digit = value.replace(prevValue, '');

      if (!digit.match(digitRegex)) {
        return;
      }

      const newCode = [...values.code];
      newCode[index] = digit;
      setFieldValue('code', newCode);

      if (index < 3) {
        inputRefs.current[index + 1].current?.focus();
      }
    },
    [values],
  );

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <Title
        text={t.title}
        forceColumn
        className={styles.title}
        dataTest="twoFA-title"
      />
      <div className={styles.desc} data-test="twoFA-desc">
        {t.desc}
      </div>
      <div className={styles['input-title']} data-test="twoFA-inputTitle">
        {t.input_title}
      </div>
      <Spacer marginBottom="20px" />
      {(error2faEmail || error2faCode) && (
        <h4
          className={styles['error-message']}
          data-test={`twoFA-error ${error2faCode}`}
        >
          {error2faCode ? t.errors[error2faCode] : error2faEmail}
        </h4>
      )}
      <div className={styles['input-group']} data-test="twoFA-inputGroup">
        {values.code.map((digit: string, index: number) => (
          <input
            name={`otp${index}`}
            key={index}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={digit}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              handleChangeInput(event.target.value, index);
            }}
            data-test={`twoFA-otp-${index}-digit`}
            ref={inputRefs.current[index]}
            className={classNames(
              styles['input-digit'],
              error2faCode && styles.error,
            )}
          />
        ))}
      </div>
      <Spacer marginBottom="20px" />
      <LinkActor
        linkProps={{
          className: styles.link,
          onClick: resendEMAIL,
          disabled: secondsLeftToResendSMS > 0,
          dataTest: 'twoFA-resendEMAIL',
        }}
      >
        {`${t.resend_code} ${
          availableEmail ? '' : `(${secondsLeftToResendSMS})`
        }`}
      </LinkActor>
      <Spacer marginTop="30px" marginBottom="26px">
        <Button
          className={styles.submit}
          loading={status2faCode === 'loading' || isLoggedIn}
          disabled={values.code.findIndex((digit) => !digit.length) >= 0}
          fill
          dataTest="twoFA-submit"
          type="submit"
          onClick={() => onSubmit(values)}
        >
          {t.cto}
        </Button>
      </Spacer>

      <div
        className={styles.footer}
        dangerouslySetInnerHTML={{ __html: t.footer }}
        data-test="twoFA-footer"
      ></div>

      <Spacer marginTop="32px" />
      <div className={styles.notification} data-test="twoFA-notification">
        <InfoIcon data-test="twoFA-notification-icon" />
        <span data-test="twoFA-notification-text">{t.notif}</span>
      </div>
    </form>
  );
};

export default TwoFAForm;
